import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import { customStyles } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';

const alertaSchema = Yup.object().shape({
  codigo_grupo_empresa: Yup.number()
    .typeError('GE é obrigatório')
    .required('GE é obrigatório')
    .positive('GE inválido')
    .integer(),
  codigo_conta: Yup.number()
    .typeError('Cliente é obrigatório')
    .required('Cliente é obrigatório')
    .positive('Cliente inválido')
    .integer(),
  texto: Yup.string()
    .trim()
    .required('Texto é obrigatório')
    .min(1, 'Texto não pode ser vazio'),
  expira: Yup.string()
    .required('Data de expiração é obrigatória')
    .test('data-valida', 'Data inválida (use AAAA-MM-DD)', (val) => {
      return val && moment(val, 'YYYY-MM-DD', true).isValid();
    }),
});

const ModalAlerta = ({
  aberto,
  modo,
  alertaEditar,
  codigoGEFiltro,
  onFechar,
  onSalvar,
}) => {
  const [opcoesGE, setOpcoesGE] = useState([]);
  const [opcoesCliente, setOpcoesCliente] = useState([]);
  const [carregandoClientes, setCarregandoClientes] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const isEdicao = modo === 'edit';

  useEffect(() => {
    if (!aberto) return;
    const carregarGEs = async () => {
      try {
        const res = await api.listarTodosGruposEmpresa();
        const lista = res || [];
        setOpcoesGE(
          lista.map((ge) => ({
            value: ge.codigo,
            label: ge.apelido,
          }))
        );
      } catch (e) {
        toast.error('Erro ao carregar grupos de empresa.');
      }
    };
    carregarGEs();
  }, [aberto]);

  const carregarClientes = async (codigoGE) => {
    if (!codigoGE) {
      setOpcoesCliente([]);
      return;
    }
    setCarregandoClientes(true);
    try {
      const res = await api.listarClientesPorGrupoEmpresa(codigoGE);
      const lista = res || [];
      setOpcoesCliente(
        lista.map((c) => ({ value: c.codigo, label: c.apelido }))
      );
    } catch (e) {
      toast.error('Erro ao carregar clientes.');
    } finally {
      setCarregandoClientes(false);
    }
  };

  useEffect(() => {
    if (!aberto) return;
    if (isEdicao && alertaEditar) {
      carregarClientes(alertaEditar.codigo_grupo_empresa);
    } else if (codigoGEFiltro) {
      carregarClientes(codigoGEFiltro);
    }
  }, [aberto, isEdicao, alertaEditar, codigoGEFiltro]);

  const valoresIniciais = isEdicao && alertaEditar
    ? {
        codigo_grupo_empresa: alertaEditar.codigo_grupo_empresa || '',
        codigo_conta: alertaEditar.codigo_conta || '',
        texto: alertaEditar.texto || '',
        expira: alertaEditar.expira
          ? moment(alertaEditar.expira).format('YYYY-MM-DD')
          : '',
      }
    : {
        codigo_grupo_empresa: codigoGEFiltro || '',
        codigo_conta: '',
        texto: '',
        expira: '',
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSalvando(true);
    try {
      // Verificação de unicidade antes de submeter
      const seqVerif = isEdicao && alertaEditar ? alertaEditar.seq : 0;
      const resUniq = await api.verificarUnicidadeAlerta({
        ge: values.codigo_grupo_empresa,
        codigoConta: values.codigo_conta,
        seq: seqVerif,
      });
      if (resUniq && resUniq.existe) {
        toast.error('Já existe alerta válido para esse cliente, verifique!');
        return;
      }

      const payload = {
        texto: values.texto.trim(),
        expira: values.expira,
      };

      if (isEdicao) {
        payload.seq = alertaEditar.seq;
        await api.alterarAlertaCliente(payload);
        toast.success('Alerta alterado com sucesso.');
      } else {
        payload.codigo_grupo_empresa = values.codigo_grupo_empresa;
        payload.codigo_conta = values.codigo_conta;
        await api.criarAlertaCliente(payload);
        toast.success('Alerta criado com sucesso.');
      }
      onSalvar();
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao salvar alerta.';
      toast.error(mensagem);
    } finally {
      setSalvando(false);
      setSubmitting(false);
    }
  };

  return (
    <Modal show={aberto} onHide={onFechar} centered size="lg">
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#0747a6', color: '#fff' }}
      >
        <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
          {isEdicao ? 'Editar Alerta' : 'Novo Alerta'}
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={alertaSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fSubmit, setFieldValue, isSubmitting }) => (
          <Form onSubmit={fSubmit}>
            <Modal.Body>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      GE <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      options={opcoesGE}
                      value={
                        opcoesGE.find(
                          (o) => o.value === values.codigo_grupo_empresa
                        ) || null
                      }
                      onChange={(selected) => {
                        setFieldValue(
                          'codigo_grupo_empresa',
                          selected ? selected.value : ''
                        );
                        setFieldValue('codigo_conta', '');
                        carregarClientes(selected ? selected.value : null);
                      }}
                      placeholder="Selecione..."
                      isClearable
                      isDisabled={isEdicao}
                      styles={customStyles}
                    />
                    {touched.codigo_grupo_empresa &&
                      errors.codigo_grupo_empresa && (
                        <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                          {errors.codigo_grupo_empresa}
                        </div>
                      )}
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Cliente <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                      options={opcoesCliente}
                      value={
                        opcoesCliente.find(
                          (o) => o.value === values.codigo_conta
                        ) || null
                      }
                      onChange={(selected) => {
                        setFieldValue(
                          'codigo_conta',
                          selected ? selected.value : ''
                        );
                      }}
                      placeholder={
                        values.codigo_grupo_empresa
                          ? carregandoClientes
                            ? 'Carregando...'
                            : 'Selecione...'
                          : 'Selecione um GE primeiro'
                      }
                      isClearable
                      isDisabled={isEdicao || !values.codigo_grupo_empresa || carregandoClientes}
                      styles={customStyles}
                    />
                    {touched.codigo_conta && errors.codigo_conta && (
                      <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                        {errors.codigo_conta}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label className="small mb-1">
                  Texto <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="texto"
                  value={values.texto}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Descreva o alerta..."
                  isInvalid={touched.texto && !!errors.texto}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.texto}
                </Form.Control.Feedback>
              </Form.Group>
              <Row>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Data de Expiração <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="expira"
                      value={values.expira}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.expira && !!errors.expira}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.expira}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onFechar}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || salvando}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalAlerta;
